/*
Snap media blocks onto the baseline grid in ruled-paper articles.

Text blocks already sit on the grid: their line-heights and margins are whole
multiples of --leading (see layouts/article.css), so the ruled background lines
line up with every text line. Images and embeds, though, have arbitrary heights
— so the content *after* a piece of media drifts off the lines.

This measures each media block inside `.tag-hash-type-text .gh-content` and pads
its bottom margin up to the next grid line, so whatever follows lands back on the
grid. The media itself keeps its natural size (no cropping); only the gap beneath
it is grown by up to one line.

Re-runs whenever heights can change: media load, web-fonts ready, and (debounced)
window resize. No-op on pages without the ruled grid.
*/

const MEDIA = 'img, picture, video, iframe, object, embed';

// Resolve --leading (or the element's line-height) to pixels.
function leadingPx(el) {
    const cs = getComputedStyle(el);
    const raw = cs.getPropertyValue('--leading').trim();
    if (raw.endsWith('rem')) {
        const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return parseFloat(raw) * rootPx;
    }
    if (raw.endsWith('px')) {
        return parseFloat(raw);
    }
    return parseFloat(cs.lineHeight) || 24;
}

// Grow the bottom margin of each media block so the next block starts on a grid line.
function snap(content) {
    const leading = leadingPx(content);
    if (!leading) { return; }

    // The grid origin is the top of the content box (where the ruled bg starts).
    const origin = content.getBoundingClientRect().top;

    Array.from(content.children).forEach(function (el) {
        const isMedia = el.matches(MEDIA) || el.querySelector(MEDIA);
        if (!isMedia) { return; }

        // Clear any previous snap so we measure against the CSS margin, then read
        // the media box bottom (getBoundingClientRect excludes the margin itself).
        el.style.marginBottom = '';
        const base = parseFloat(getComputedStyle(el).marginBottom) || 0;
        const bottom = el.getBoundingClientRect().bottom;

        let remainder = (bottom - origin) % leading;
        if (remainder < 0) { remainder += leading; }

        const correction = (leading - remainder) % leading;
        if (correction > 0.5) {
            el.style.marginBottom = (base + correction) + 'px';
        }
    });
}

export default function baselineGrid() {
    const contents = Array.from(document.querySelectorAll('.tag-hash-type-text .gh-content'));
    if (!contents.length) { return; }

    const run = () => contents.forEach(snap);

    run();

    // Media whose height only settles after it loads.
    contents.forEach(function (content) {
        content.querySelectorAll(MEDIA).forEach(function (m) {
            if (m.complete) { return; }
            m.addEventListener('load', run, { once: true });
            m.addEventListener('loadedmetadata', run, { once: true });
        });
    });

    // Font swap changes text metrics (and therefore where media lands).
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(run);
    }

    // Re-snap on resize (widths change embed heights), debounced.
    let raf = 0;
    window.addEventListener('resize', function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(run);
    });
}
